/**
 * Loading OnlyFans (and Fansly) media in the browser (port of the CRM's
 * shared/media-proxy.js and fansly-crm/lib/media-proxy.js).
 *
 * OnlyFans signs CDN URLs and may lock the signature to the account's proxy IP
 * (a `SourceIp` condition in the CloudFront policy). A browser on another IP
 * gets 403 for those, so they are fetched through the CRM (`/v1/media/cdn-fetch`,
 * which egresses via the account proxy) and shown as blob URLs. Everything else
 * loads directly.
 */

export type CdnSource =
  | { kind: 'direct'; src: string }
  | { kind: 'blob'; src: string }
  | { kind: 'blocked' }

export interface CdnMediaOptions {
  /** POST to a CRM-local route as the signed-in user. */
  post: (accountId: string, path: string, body: Record<string, unknown>) => Promise<Response>
  createObjectURL?: (blob: Blob) => string
  /** Whose CDN this is (default OnlyFans): its hosts and the CRM routes that reach it. */
  cdn?: CdnRoutes
}

/** One platform's CDN as the CRM proxies it. */
export interface CdnRoutes {
  /** Hosts whose signed URLs may be locked to the account's proxy IP. */
  isCdnHost: (hostname: string, pathname: string) => boolean
  capability: (accountId: string) => string
  fetch: (accountId: string) => string
  streamSession: (accountId: string) => string
}

/** OnlyFans media: the CRM's /v1/media routes. */
export const ONLYFANS_CDN: CdnRoutes = {
  isCdnHost: (host) => /(^|\.)onlyfans\.com$/i.test(host),
  capability: () => '/v1/media/cdn-capability',
  fetch: () => '/v1/media/cdn-fetch',
  streamSession: () => '/v1/media/cdn-stream-session',
}

/** Fansly media: the CRM's per-account fansly-cdn routes (not its API hosts). */
export const FANSLY_CDN: CdnRoutes = {
  isCdnHost: (host, path) =>
    /(^|\.)fansly\.com$/i.test(host) && !/^(apiv\d+|mediav\d+|leaderboard)\.fansly\.com$/i.test(host) && !path.startsWith('/api/'),
  capability: (id) => `/api/accounts/${encodeURIComponent(id)}/fansly-cdn-capability`,
  fetch: (id) => `/api/accounts/${encodeURIComponent(id)}/fansly-cdn-fetch`,
  streamSession: (id) => `/api/accounts/${encodeURIComponent(id)}/fansly-cdn-stream-session`,
}

export interface CdnMedia {
  /**
   * The media's displayable source. With a `signal`, a fetch still waiting
   * for a slot is dropped (AbortError) once every caller that passed one has
   * aborted and nobody asked without one: a closed grid's queue does not
   * hold up the next grid.
   */
  load: (accountId: string, url: string, signal?: AbortSignal) => Promise<CdnSource>
  /** A playable URL for video/audio: OnlyFans files stream through the CRM. */
  stream: (accountId: string, url: string) => Promise<string>
}

/** More parallel CDN fetches starved the vault grid (PR #13 measurements). */
const MAX_CONCURRENT_FETCHES = 4

/** One file's fetch, shared by everyone who asks for it. */
interface Job {
  result: Promise<CdnSource>
  abort: AbortController
  /** Callers that may still give up on it (they passed a signal). */
  waiters: number
  /** Someone asked without a signal: it always runs. */
  pinned: boolean
  /** Holds a slot: too late to drop. */
  started: boolean
}

const abortError = () => new DOMException('The media is no longer needed', 'AbortError')

/** CloudFront writes base64 with -, _ and ~ in place of +, = and /. */
function decodePolicy(policy: string): string {
  try {
    let b64 = policy.replace(/-/g, '+').replace(/_/g, '=').replace(/~/g, '/')
    if (b64.length % 4) b64 += '='.repeat(4 - (b64.length % 4))
    return atob(b64)
  } catch {
    return ''
  }
}

function isIpLocked(url: URL, cdn: CdnRoutes): boolean {
  if (!cdn.isCdnHost(url.hostname, url.pathname)) return false
  const policy = url.searchParams.get('Policy')
  return !!policy && /SourceIp|IpAddress/i.test(decodePolicy(policy))
}

export function createCdnMedia({
  post,
  createObjectURL = (b) => URL.createObjectURL(b),
  cdn = ONLYFANS_CDN,
}: CdnMediaOptions): CdnMedia {
  // OnlyFans re-signs URLs on every listing; the path names the file.
  const byFile = new Map<string, Job>()
  // Without a configured proxy the CRM cannot fetch IP-locked media either.
  const proxyByAccount = new Map<string, Promise<boolean>>()

  const hasProxy = (accountId: string) => {
    let known = proxyByAccount.get(accountId)
    if (!known) {
      known = post(accountId, cdn.capability(accountId), { account_id: accountId })
        .then(async (res) => {
          type Capability = { has_proxy?: unknown }
          const body = (await res.json().catch(() => null)) as (Capability & { data?: Capability }) | null
          return res.ok && (body?.data ?? body)?.has_proxy === true
        })
        .catch(() => false)
      proxyByAccount.set(accountId, known)
    }
    return known
  }

  let active = 0
  const waiting: Array<() => void> = []
  const slot = async <T>(work: () => Promise<T>, signal: AbortSignal): Promise<T> => {
    if (signal.aborted) throw abortError()
    if (active >= MAX_CONCURRENT_FETCHES) {
      await new Promise<void>((resolve, reject) => {
        const go = () => resolve()
        waiting.push(go)
        signal.addEventListener(
          'abort',
          () => {
            const i = waiting.indexOf(go)
            if (i < 0) return // already handed a slot: it runs
            waiting.splice(i, 1)
            reject(abortError())
          },
          { once: true },
        )
      })
    }
    active += 1
    try {
      return await work()
    } finally {
      active -= 1
      waiting.shift()?.()
    }
  }

  const fetchThroughCrm = async (accountId: string, raw: string, job: Job): Promise<CdnSource> => {
    if (!(await hasProxy(accountId))) return { kind: 'blocked' }
    return slot(async () => {
      job.started = true
      const res = await post(accountId, cdn.fetch(accountId), { account_id: accountId, url: raw })
      if (!res.ok) throw new Error(`CDN fetch failed (${res.status})`)
      return { kind: 'blob', src: createObjectURL(await res.blob()) } as const
    }, job.abort.signal)
  }

  return {
    async stream(accountId, raw) {
      let host = ''
      try {
        host = new URL(raw).hostname
      } catch {
        return raw
      }
      let path = ''
      try {
        path = new URL(raw).pathname
      } catch {
        return raw
      }
      if (!cdn.isCdnHost(host, path)) return raw
      const res = await post(accountId, cdn.streamSession(accountId), { account_id: accountId, url: raw })
      type Session = { stream_url?: unknown; detail?: unknown }
      const body = (await res.json().catch(() => null)) as (Session & { data?: Session }) | null
      const streamUrl = (body?.data ?? body)?.stream_url
      if (!res.ok || typeof streamUrl !== 'string') throw new Error(String((body?.data ?? body)?.detail ?? 'Could not start the stream'))
      // The CRM answers with its own root-absolute path; it is served under /crm here.
      return streamUrl.startsWith('/') ? `/crm${streamUrl}` : streamUrl
    },
    load(accountId, raw, signal) {
      let url: URL
      try {
        url = new URL(raw)
      } catch {
        return Promise.resolve({ kind: 'blocked' })
      }
      if (!isIpLocked(url, cdn)) return Promise.resolve({ kind: 'direct', src: raw })
      const key = `${accountId}|${url.origin}${url.pathname}`
      let job = byFile.get(key)
      if (!job) {
        const fresh: Job = { result: Promise.resolve({ kind: 'blocked' }), abort: new AbortController(), waiters: 0, pinned: false, started: false }
        fresh.result = fetchThroughCrm(accountId, raw, fresh)
        fresh.result.catch(() => byFile.get(key) === fresh && byFile.delete(key))
        byFile.set(key, fresh)
        job = fresh
      }
      if (!signal) job.pinned = true
      else {
        const j = job
        j.waiters += 1
        const leave = () => {
          j.waiters -= 1
          if (!j.waiters && !j.pinned && !j.started) j.abort.abort()
        }
        if (signal.aborted) leave()
        else signal.addEventListener('abort', leave, { once: true })
      }
      return job.result
    },
  }
}
